import { ArrowBack, ArrowForward } from '@mui/icons-material';
import { Alert, Box, Button, Paper, Step, StepLabel, Stepper, Typography } from '@mui/material';
import { JSX, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { exportBodyDiagram } from '../../../library/bodyDiagramHelper';
import { useValidatedForm } from '../../../library/formValidation';
import { useApiFormError } from '../../../library/useApiFormError';
import { useStepValidity } from '../../../library/useStepValidity';
import {
    CreateEvaluationRequest,
    EvaluationFormValues,
    IEvaluation,
    toCreateEvaluationRequest,
} from '../../../library/models/evaluation';
import ResourcesIds from '../../../resources/ResourcesIds';
import { useReactMutation } from '../../../services/queryService';
import { services } from '../../../services/services';
import { useSnackbar } from '../../layout/snackbarProvider';
import {
    alertStyles,
    formWrapperStyles,
    headerBoxStyles,
    navigationButtonsBoxStyles,
    stepperStyles,
    subtitleStyles,
    titleStyles,
} from './evaluation.styles';
import { createEvaluationSchema } from './evaluationSchema';
import { AssessmentStep } from './steps/AssessmentStep';
import { ConsentStep } from './steps/ConsentStep';
import { GdprStep } from './steps/GdprStep';

/**
 * Which fields each step owns.
 *
 * Continue validates only the step being left, so a therapist is never told about a
 * missing consent while they are still typing the patient's name. The final step submits
 * instead, and the resolver checks everything.
 */
const ASSESSMENT_FIELDS: (keyof EvaluationFormValues)[] =
    ['emailAddress', 'firstName', 'lastName', 'phoneNumber', 'occupation', 'age', 'sex',
        'evaluatedOn', 'reasonForVisit', 'investigations', 'painLevel', 'painCharacter',
        'medication', 'injuryHistory', 'notes'];

/**
 * The signature and its printed name are here rather than with the agreement, because
 * what they sign is the treatment consent -- which is given at every visit, where the
 * agreement is given once.
 */
const CONSENT_FIELDS: (keyof EvaluationFormValues)[] =
    ['patientAddress', 'idSeries', 'idNumber', 'consentCapacity', 'childAge',
        'referringDoctor', 'treatmentConsentGiven', 'signedName',
        'signature'];

const GDPR_FIELDS: (keyof EvaluationFormValues)[] = ['gdprConsentGiven'];

interface EvaluationWizardProps {
    /**
     * What the form starts holding. Whole rather than patched in afterwards: a `reset`
     * once a fetch lands would race the therapist and wipe whatever they had already
     * typed, so the seed is resolved before this ever mounts.
     */
    initialValues: EvaluationFormValues;
    /** A returning patient's next sheet, rather than a stranger's first one. */
    isFollowUp: boolean;
    /**
     * Whether to collect the data-processing agreement, which decides whether this is a
     * three-step wizard or a two-step one.
     *
     * Resolved by `Evaluation` from what the API has on file, and deliberately not the
     * same question as `isFollowUp`: that one only says the URL carried a patient id, and
     * somebody an admin invited reaches it having agreed to nothing. Advisory in any case
     * -- the API asks again before it stores anything.
     */
    requiresGdprConsent: boolean;
    /** Where the therapist belongs once the sheet is filed. */
    onSaved: () => void;
}

/**
 * The intake pack itself: the evaluation sheet, the consent to treatment, and -- for a
 * patient who has not given it here before -- the data-processing agreement, as steps of
 * one form.
 *
 * <p>It is three steps or two. The agreement is given once per patient per clinic, so a
 * returning patient is shown the assessment and the consent and nothing else. Both the
 * step list and the schema are built from `requiresGdprConsent`, and the schema half is
 * load-bearing: `useStepValidity` filters failures by step and would ignore a rule no step
 * owns, but `handleSubmit` runs the resolver over everything, so a `.oneOf([true])` left
 * standing on a hidden field would leave Save dead with nothing on screen to explain it.
 *
 * <p>Submitting does considerably more than save a row. The API creates and invites the
 * patient's account, renders the documents to a PDF, files it, and emails the patient a
 * link to their copy -- which is why the email address is the one field on the first step
 * that cannot be left blank.
 *
 * <p>It knows nothing about routes or about which patient it is for. `Evaluation` answers
 * both, so that a blank form and a follow-up are the same wizard given different values
 * rather than two screens that have to be kept in step.
 */
export const EvaluationWizard = (props: EvaluationWizardProps): JSX.Element => {
    const { initialValues, isFollowUp, requiresGdprConsent, onSaved } = props;
    const { t: translate } = useTranslation();
    const { showSnackbar } = useSnackbar();

    const [activeStep, setActiveStep] = useState<number>(0);

    const schema = useMemo(() => createEvaluationSchema(translate, requiresGdprConsent),
        [translate, requiresGdprConsent]);

    const stepFields: (keyof EvaluationFormValues)[][] = useMemo(
        () => requiresGdprConsent
            ? [ASSESSMENT_FIELDS, CONSENT_FIELDS, GDPR_FIELDS]
            : [ASSESSMENT_FIELDS, CONSENT_FIELDS],
        [requiresGdprConsent]);

    const {
        register,
        control,
        handleSubmit,
        trigger,
        setError,
        formState: { errors, isSubmitting },
    } = useValidatedForm<EvaluationFormValues>(schema, { defaultValues: initialValues });

    const knownFields: string[] = useMemo(() => Object.keys(schema.fields), [schema]);
    const { formError, report, clear } = useApiFormError<EvaluationFormValues>(
        setError, knownFields, translate, ResourcesIds.EVALUATION_SAVE_ERROR);

    /**
     * Whether the step on screen may be left. Not `formState.isValid`, which is whole-form
     * and would hold the signature on the last step against a therapist still filling in
     * the first one -- see useStepValidity.
     */
    const values = useWatch({ control }) as EvaluationFormValues;
    const canContinue: boolean = useStepValidity(schema, values, stepFields[activeStep]);

    const evaluationMutation = useReactMutation<IEvaluation, Error, CreateEvaluationRequest>(
        services.evaluations.createEvaluationMutation()
    );

    const steps: string[] = [
        translate(ResourcesIds.EVALUATION_STEP_ASSESSMENT),
        translate(ResourcesIds.EVALUATION_STEP_CONSENT),
        ...(requiresGdprConsent ? [translate(ResourcesIds.EVALUATION_STEP_GDPR)] : []),
    ];

    const isLastStep: boolean = activeStep === steps.length - 1;
    const isSigningForChild: boolean = values.consentCapacity === 'LEGAL_REPRESENTATIVE';

    /**
     * Continue is disabled until the step is valid, so this can only fire on a good one.
     * The `trigger` stays anyway: it validates the values react-hook-form holds rather
     * than the snapshot the button was rendered from, so a stale render cannot advance
     * the wizard past a bad step.
     */
    const handleNext = async (): Promise<void> => {
        clear();

        if (await trigger(stepFields[activeStep])) {
            setActiveStep((step) => step + 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handleBack = (): void => {
        clear();
        setActiveStep((step) => Math.max(0, step - 1));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const onValid = async (submitted: EvaluationFormValues): Promise<void> => {
        clear();

        try {
            await evaluationMutation.mutateAsync(
                toCreateEvaluationRequest(submitted, exportBodyDiagram(submitted.bodyDiagram))
            );

            showSnackbar(translate(ResourcesIds.EVALUATION_SAVED_SUCCESS), 'success');
            onSaved();
        } catch (error: unknown) {
            report(error);
        }
    };

    return (
        <Box>
            <Box sx={headerBoxStyles}>
                <Box>
                    <Typography variant="h4" component="h1" sx={titleStyles}>
                        {translate(ResourcesIds.EVALUATION_TITLE)}
                    </Typography>
                    <Typography variant="body1" sx={subtitleStyles}>
                        {translate(isFollowUp
                            ? ResourcesIds.EVALUATION_FOLLOW_UP_SUBTITLE
                            : ResourcesIds.EVALUATION_SUBTITLE)}
                    </Typography>
                </Box>
            </Box>

            <Stepper activeStep={activeStep} sx={stepperStyles}>
                {steps.map((label) => (
                    <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                    </Step>
                ))}
            </Stepper>

            <Paper sx={formWrapperStyles}>
                {formError && <Alert severity="error" sx={alertStyles}>{formError}</Alert>}

                {/* One form across all three steps. Only the current step is mounted;
                    react-hook-form keeps the values of the others, because
                    shouldUnregister defaults to false -- so going back to correct a name
                    does not lose anything typed after it. */}
                <form onSubmit={handleSubmit(onValid)} noValidate>
                    {activeStep === 0 && (
                        <AssessmentStep
                            register={register}
                            control={control}
                            errors={errors}
                            lockEmail={isFollowUp}
                        />
                    )}

                    {activeStep === 1 && (
                        <ConsentStep
                            register={register}
                            control={control}
                            errors={errors}
                            isSigningForChild={isSigningForChild}
                        />
                    )}

                    {activeStep === 2 && <GdprStep control={control} errors={errors} />}

                    <Box sx={navigationButtonsBoxStyles}>
                        <Button
                            onClick={handleBack}
                            variant="outlined"
                            startIcon={<ArrowBack />}
                            disabled={activeStep === 0 || isSubmitting}
                        >
                            {translate(ResourcesIds.BACK_BUTTON)}
                        </Button>

                        {isLastStep ? (
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={!canContinue || isSubmitting}
                            >
                                {translate(ResourcesIds.EVALUATION_SUBMIT)}
                            </Button>
                        ) : (
                            <Button
                                onClick={handleNext}
                                variant="contained"
                                endIcon={<ArrowForward />}
                                disabled={!canContinue || isSubmitting}
                            >
                                {translate(ResourcesIds.CONTINUE_BUTTON)}
                            </Button>
                        )}
                    </Box>
                </form>
            </Paper>
        </Box>
    );
};
